/**
 * Workload breaches — too many days in a row, or too many hours on average.
 * Taking this shift off the guard is the dependable fix; for the averages,
 * shortening the shift by the excess can be enough on its own.
 */

import { findViolationShift, numberDetail, snapDown } from '../rosterModel';
import type { RemediationStrategy, RosterRuleViolation, StrategyResult } from '../types';
import { reassignShiftProposals, releaseShiftProposals } from './fixBuilders';

/** Minutes over the rule's allowance across its reference period, when the breach reports them. */
export function excessReferenceMinutes(violation: RosterRuleViolation): number | null {
  const total = numberDetail(violation, 'total_minutes');
  if (total === null) return null;

  switch (violation.rule_code) {
    case 'max_average_weekly_hours': {
      const weekly = numberDetail(violation, 'limit_weekly_minutes');
      const weeks = numberDetail(violation, 'reference_weeks');
      return weekly !== null && weeks !== null ? total - (weekly * weeks) : null;
    }
    case 'night_work_average_weekly_hours': {
      const weekly = numberDetail(violation, 'limit_weekly_minutes');
      const days = numberDetail(violation, 'reference_days');
      return weekly !== null && days !== null ? Math.ceil(total - ((weekly / 7) * days)) : null;
    }
    case 'night_work_average_24h_hours': {
      const daily = numberDetail(violation, 'limit_24h_minutes');
      const days = numberDetail(violation, 'reference_days');
      return daily !== null && days !== null ? total - (daily * days) : null;
    }
    default:
      return null;
  }
}

export const workloadStrategy: RemediationStrategy = {
  ruleCodes: [
    'max_consecutive_work_days',
    'max_average_weekly_hours',
    'night_work_average_weekly_hours',
    'night_work_average_24h_hours',
  ],

  async propose(violation, context): Promise<StrategyResult> {
    const shift = findViolationShift(violation, context.snapshot);
    if (!shift) return { proposals: [], unsupportedReason: 'shift_not_on_roster' };

    const proposals = await reassignShiftProposals(shift, context, { limit: 3, preference: 0 });

    const excess = excessReferenceMinutes(violation);
    if (excess !== null && excess > 0) {
      proposals.push(...await releaseShiftProposals(shift, context, {
        side: 'end',
        boundaryMinute: snapDown(shift.endMinute - excess),
        handoverLimit: 1,
        preference: 10,
      }));
    }

    return proposals.length > 0 ? { proposals } : { proposals, unsupportedReason: 'no_eligible_guards' };
  },
};
